import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import { Plus, Eye, Trash, PencilSimple } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

export default function APQPPage() {
  const [projects, setProjects] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { user } = useAuth();

  // Check if user has full access (can edit/delete/create)
  // Admin and QA Manager always have full access
  // Other users have edit access if access_level === 'edit'
  const hasFullAccess = user?.role === 'admin' || user?.role === 'qa_manager' || user?.access_level === 'edit';

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      const response = await api.get('/apqp/projects');
      setProjects(response.data);
    } catch (error) {
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProject = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    try {
      await api.post('/apqp/projects', {
        project_name: formData.get('project_name'),
        customer: formData.get('customer'),
        part_number: formData.get('part_number'),
      });
      toast.success('Project created successfully');
      setShowCreateModal(false);
      loadProjects();
    } catch (error) {
      toast.error('Failed to create project');
    }
  };

  const handleEditProject = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    try {
      await api.put(`/apqp/projects/${editingProject.id}`, {
        project_name: formData.get('project_name'),
        customer: formData.get('customer'),
        part_number: formData.get('part_number'),
      });
      toast.success('Project updated successfully');
      setShowEditModal(false);
      setEditingProject(null);
      loadProjects();
    } catch (error) {
      toast.error('Failed to update project');
    }
  };

  const handleDeleteProject = async (projectId) => {
    if (!window.confirm('Are you sure you want to delete this APQP project? This action cannot be undone.')) {
      return;
    }
    try {
      await api.delete(`/apqp/projects/${projectId}`);
      toast.success('Project deleted successfully');
      loadProjects();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete project');
    }
  };

  const getPhaseLabel = (phase) => {
    const phases = {
      1: 'Plan & Define',
      2: 'Product Design',
      3: 'Process Design',
      4: 'Validation',
      5: 'Feedback & Assessment'
    };
    return phases[phase] || 'Unknown';
  };

  return (
    <div data-testid="apqp-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-gray-900">APQP Management</h1>
          <p className="mt-1 text-sm text-gray-600">Advanced Product Quality Planning projects</p>
        </div>
        {hasFullAccess && (
          <button
            data-testid="create-apqp-button"
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 bg-gray-900 text-white px-4 py-2 rounded-sm hover:bg-gray-800 transition-colors"
          >
            <Plus size={20} weight="bold" />
            New Project
          </button>
        )}
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-500">Loading projects...</div>
      ) : projects.length === 0 ? (
        <div className="text-center py-12 bg-white border border-gray-200 rounded-sm">
          <p className="text-gray-500">No APQP projects yet. Create one to get started.</p>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-sm overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase tracking-wider">Project Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase tracking-wider">Customer</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase tracking-wider">Part Number</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase tracking-wider">Current Phase</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {projects.map((project, idx) => (
                <tr key={project.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} data-testid={`apqp-project-row-${idx}`}>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">{project.project_name}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">{project.customer}</td>
                  <td className="px-6 py-4 text-sm text-gray-600 mono">{project.part_number}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    <span className="font-medium">Phase {project.current_phase}:</span> {getPhaseLabel(project.current_phase)}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 text-xs font-medium rounded-sm ${
                      project.status === 'active' ? 'bg-blue-100 text-blue-700' :
                      project.status === 'completed' ? 'bg-green-100 text-green-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {project.status.toUpperCase()}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <button
                        data-testid={`view-apqp-${idx}`}
                        onClick={() => navigate(`/apqp/${project.id}`)}
                        className="flex items-center gap-1 text-blue-600 hover:text-blue-700 text-sm font-medium"
                      >
                        <Eye size={16} /> View
                      </button>
                      {hasFullAccess && (
                        <>
                          <button
                            data-testid={`edit-apqp-${idx}`}
                            onClick={() => {
                              setEditingProject(project);
                              setShowEditModal(true);
                            }}
                            className="flex items-center gap-1 text-gray-600 hover:text-gray-700 text-sm font-medium"
                          >
                            <PencilSimple size={16} /> Edit
                          </button>
                          <button
                            data-testid={`delete-apqp-${idx}`}
                            onClick={() => handleDeleteProject(project.id)}
                            className="flex items-center gap-1 text-red-600 hover:text-red-700 text-sm font-medium"
                          >
                            <Trash size={16} /> Delete
                          </button>
                        </>
                      )}
                      {!hasFullAccess && (
                        <span className="text-xs text-gray-400 italic">View Only</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-sm w-full max-w-md">
            <div className="border-b border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900">Create New APQP Project</h2>
            </div>
            <form onSubmit={handleCreateProject} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">PROJECT NAME</label>
                <input
                  name="project_name"
                  data-testid="apqp-project-name-input"
                  type="text"
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">CUSTOMER</label>
                <input
                  name="customer"
                  data-testid="apqp-customer-input"
                  type="text"
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">PART NUMBER</label>
                <input
                  name="part_number"
                  data-testid="apqp-part-number-input"
                  type="text"
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                />
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-sm hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  data-testid="apqp-create-submit"
                  className="flex-1 px-4 py-2 bg-gray-900 text-white rounded-sm hover:bg-gray-800 transition-colors"
                >
                  Create Project
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showEditModal && editingProject && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-sm w-full max-w-md">
            <div className="border-b border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900">Edit APQP Project</h2>
            </div>
            <form onSubmit={handleEditProject} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">PROJECT NAME</label>
                <input
                  name="project_name"
                  data-testid="apqp-edit-project-name-input"
                  type="text"
                  defaultValue={editingProject.project_name}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">CUSTOMER</label>
                <input
                  name="customer"
                  data-testid="apqp-edit-customer-input"
                  type="text"
                  defaultValue={editingProject.customer}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">PART NUMBER</label>
                <input
                  name="part_number"
                  data-testid="apqp-edit-part-number-input"
                  type="text"
                  defaultValue={editingProject.part_number}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                />
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingProject(null);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-sm hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  data-testid="apqp-edit-submit"
                  className="flex-1 px-4 py-2 bg-gray-900 text-white rounded-sm hover:bg-gray-800 transition-colors"
                >
                  Update Project
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
